import { Clock } from 'lucide-react';
import { formatTimeLeft, lateQueueMessage, type LateQueueStage } from '../../utils/lateQueueWarning';

interface LateQueueWarningProps {
  stage: LateQueueStage;
  remainingMs: number;
  playerLabel: string;
}

// The next song's card outline escalates with the warning: steady amber, then flashing amber, then flashing red
const CARD_OUTLINES: Record<LateQueueStage, string | null> = {
  caution: null,
  warning: 'ring-2 ring-amber-500 motion-safe:animate-ring-pulse-amber',
  strong: 'ring-2 ring-red-500 motion-safe:animate-ring-pulse-red',
};

export function lateQueueCardOutline(stage: LateQueueStage | null): string | null {
  return stage ? CARD_OUTLINES[stage] : null;
}

const STAGE_CLASSES: Record<LateQueueStage, string> = {
  caution: 'border-l-amber-500 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
  warning: 'border-l-orange-500 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300',
  strong: 'border-l-red-500 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300',
};

// Calm, card-level caution for queuing late in a manual mode (ITER-183): never a banner, never dismissible
export function LateQueueWarning({ stage, remainingMs, playerLabel }: LateQueueWarningProps) {
  return (
    <div
      data-testid={`late-queue-${stage}`}
      className={`flex items-start gap-2 mt-2 px-2 py-1.5 rounded-md border-l-4 text-xs ${STAGE_CLASSES[stage]}`}
    >
      <Clock className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
      <span className="flex-1">{lateQueueMessage(stage, remainingMs, playerLabel)}</span>
      <span className="tabular-nums font-medium whitespace-nowrap">{formatTimeLeft(remainingMs)}</span>
    </div>
  );
}
